import { useState } from 'react';

import { SEOHead } from '@/components/SEOHead';
import { AnalyticalField } from '@/components/AnalyticalField';

const GridBg = () => (
  <div className="absolute inset-0 grid-texture pointer-events-none" aria-hidden="true" />
);

const LEGAL_SUMMARY_POINTS = [
  'B2B-only services (no consumer services).',
  'Agreement acceptance via SOW execution or engagement of Services.',
  'Analytical interpretation only; no system access; no implementation.',
  'Offline isolated Linux processing; no third-party SaaS routing; no subprocessors.',
  'Encrypted transmission required (email or secure link).',
  'Default data retention: 14 days after deliverables.',
  'GDPR Article 28 aligned; breach notification within 72 hours of awareness.',
  'Fees non-refundable except where mandatory law requires.',
  'Liability capped to fees paid under relevant SOW; indirect damages excluded; mandatory statutory carve-outs preserved.',
  'Provider may refuse/terminate where sanctions/export controls apply; Client responsible for cross-border transfer compliance and applicable taxes.',
];

const MSA_TEXT = `MASTER SERVICES AGREEMENT
ONPOINT.WORKS – WFM ANALYTICS LAB
Version 1.5
Effective Date: December 2025

This Master Services Agreement (“Agreement”) governs all services provided by:

Avyria LLC
1209 Mountain Road PL NE Ste N
Albuquerque, NM 87110
United States of America
("Service Provider")

to the legal business entity executing a Statement of Work (“Client”).

1. B2B-ONLY ENGAGEMENT

Services are offered exclusively to business entities acting in the course of commercial or professional activity.

No services are offered to consumers under:

EU Directive 2011/83/EU

German BGB consumer law

UK Consumer Rights Act

Any equivalent consumer protection legislation globally

Client represents and warrants that it is acting in a B2B capacity and not as a consumer.

2. ACCEPTANCE OF AGREEMENT

This Agreement becomes binding upon:

(a) Execution of a Statement of Work (SOW), OR
(b) Engagement of Services, OR
(c) Written acceptance referencing this Agreement.

By executing an SOW or engaging Services, Client acknowledges acceptance of the current version of this Agreement as published by Service Provider.

Service Provider may update this Agreement from time to time. The version effective on the SOW date governs that engagement.

3. SCOPE OF SERVICES

Service Provider provides independent analytical interpretation services strictly as defined in an executed Statement of Work (“SOW”).

Services are limited to:

Statistical modeling

Structural workforce instability classification

Quantitative scenario simulations

Analytical reporting

Service Provider does NOT:

Access live operational systems

Implement staffing changes

Provide legal, tax, HR, or financial advice

Guarantee operational or financial outcomes

All decisions remain solely the responsibility of Client.

4. PRE-DIAGNOSTICS SESSION (TIME-BASED SERVICE)

4.1 Nature
The Pre-Diagnostics Session is a time-based exploratory analytical engagement.

4.2 Compensation
Fees are based on reserved professional time allocation.

4.3 Earned Upon Booking
Fees are fully earned upon booking confirmation, session commencement, or client no-show.

4.4 Cancellation
Sessions cancelled within 48 hours are non-refundable.

4.5 No Obligation
No obligation to proceed to further engagement is created.

4.6 No Reliance
Statements during intake are exploratory and non-binding.

5. FEES, TAXES & NO REFUND POLICY

5.1 Fees
All fees are:

Non-refundable

Non-cancellable once work commences

Earned upon data receipt or session commencement

5.2 Taxes
Client is responsible for all applicable taxes, duties, levies, VAT, GST, sales taxes, withholding taxes, or similar charges arising from the engagement, excluding taxes based solely on Service Provider’s net income.

If withholding taxes apply, Client shall gross up payments to ensure Service Provider receives the full contracted amount unless prohibited by law.

5.3 Refunds
Refunds are only available where mandatory non-waivable statutory law requires otherwise.

6. DATA TRANSMISSION & PROCESSING ENVIRONMENT

6.1 Transmission
Client shall transmit Data via encrypted email or secure encrypted transfer link.

6.2 Processing
All processing occurs:

Offline

On isolated Linux-based systems

Without cloud hosting of client datasets

Without third-party SaaS routing

Without sub-processors

6.3 Retention
Data is permanently deleted within fourteen (14) days of Deliverable issuance unless otherwise agreed.

Deletion confirmation available upon request.

7. DATA PROCESSING AGREEMENT (ARTICLE 28 GDPR)

This Section constitutes the Data Processing Agreement under Article 28 GDPR.

7.1 Roles
Client = Data Controller
Service Provider = Data Processor

7.2 Subject Matter
Processing limited to analytical interpretation of exported workforce datasets.

7.3 Duration
Processing lasts for engagement term plus retention window.

7.4 Nature & Purpose
Statistical modeling and structural classification.

7.5 Categories of Data
Workforce-related operational data transmitted by Client.

7.6 Categories of Data Subjects
Employees, contractors, agents.

7.7 Processor Obligations
Service Provider shall:

Process data only on documented instructions

Ensure confidentiality

Implement appropriate technical and organizational measures

Assist with data subject rights where reasonably feasible

Notify Client within seventy-two (72) hours of becoming aware of a Personal Data Breach

Delete or return data after retention period

7.8 Sub-Processors
No sub-processors engaged.

7.9 Audit
Audit limited to documentation review once per calendar year with 30 days’ written notice.

7.10 International Transfers
Client acknowledges processing state independent and confirms lawful transfer mechanisms under GDPR Chapter V.

Client remains solely responsible for lawful cross-border transfer compliance.

8. SECURITY MEASURES (TECHNICAL & ORGANIZATIONAL MEASURES – TOM)

Service Provider implements:

• Offline isolated processing environment
• No cloud hosting of client datasets
• No third-party routing
• Access restricted to authorized personnel
• Device-level encryption during analysis
• Controlled deletion procedures
• Secure credential management
• Restricted physical access
• Incident response protocol
• Minimal retention policy (14 days default)

These measures reflect proportional risk mitigation aligned with offline analytical processing.

9. CONFIDENTIALITY

Both parties shall maintain confidentiality of exchanged information.

Obligations survive termination.

10. INTELLECTUAL PROPERTY

All analytical methodologies, frameworks, models, and classification systems remain exclusive property of Service Provider.

Client receives a limited, non-exclusive, non-transferable internal-use license.

Unauthorized use entitles Service Provider to injunctive and equitable relief.

11. NO WARRANTY

Services are provided “AS IS.”

All express and implied warranties are disclaimed to the fullest extent permitted by law.

Deliverables are analytical interpretations only.

12. LIMITATION OF LIABILITY

12.1 Cap
Total aggregate liability shall not exceed fees paid under the relevant SOW.

12.2 Excluded Damages
No liability for:

Lost profits

Lost revenue

Regulatory fines

Workforce disputes

Indirect or consequential damages

12.3 Mandatory Carve-Out
Nothing limits liability for:

Intent

Gross negligence

Injury to life, body, or health

Mandatory statutory liability

13. INDEMNIFICATION

Client shall indemnify and hold harmless Service Provider from claims arising from:

Operational decisions

Workforce actions

Regulatory non-compliance originating from Client

Misuse of Deliverables

14. INSURANCE

Service Provider is not required to maintain professional liability or cyber-risk insurance unless expressly required in a signed SOW.

If required, Service Provider may procure coverage for the duration of the engagement.

Insurance shall not expand liability beyond contractual limitations.

15. COMPLIANCE & SANCTIONS

15.1 Legal Compliance
Each party represents compliance with:

Anti-corruption and anti-bribery laws

Sanctions and export control regulations

Applicable labor and tax laws

15.2 Sanctions Refusal Clause
Service Provider reserves the right to refuse or terminate services where export control laws, trade sanctions, or regulatory restrictions apply.

16. INDEPENDENT CONTRACTOR

Service Provider acts as independent contractor.

No partnership, joint venture, employment, or fiduciary relationship is created.

17. FORCE MAJEURE

Neither party shall be liable for events beyond reasonable control.

18. TERM & TERMINATION

Agreement remains in effect until terminated in writing.

Termination does not affect accrued payment obligations.

19. SURVIVAL

The following survive termination:

Intellectual Property

Confidentiality

Limitation of Liability

Indemnification

Governing Law

Payment Obligations

20. ASSIGNMENT

Client may not assign without written consent.

21. WAIVER

Failure to enforce any provision does not constitute waiver.

22. AMENDMENT

Amendments must be in writing and signed by both parties.

23. SEVERABILITY

If any provision is invalid, remaining provisions remain enforceable.

24. GOVERNING LAW

For EU / German Clients:

German law applies (excluding CISG).
Exclusive jurisdiction: Frankfurt am Main.

For non-EU Clients:

New Mexico law applies.
Exclusive jurisdiction: New Mexico courts.

FINAL POSITION

Service Provider delivers analytical interpretation services only.

All operational, financial, workforce, regulatory, tax, and data compliance responsibility remains solely with Client.`;

const TOM_TEXT = `8. SECURITY MEASURES (TECHNICAL & ORGANIZATIONAL MEASURES – TOM)

Service Provider implements:

• Offline isolated processing environment
• No cloud hosting of client datasets
• No third-party routing
• Access restricted to authorized personnel
• Device-level encryption during analysis
• Controlled deletion procedures
• Secure credential management
• Restricted physical access
• Incident response protocol
• Minimal retention policy (14 days default)

These measures reflect proportional risk mitigation aligned with offline analytical processing.`;

function AccordionSection({ id, label, isOpen, onToggle, children }) {
  return (
    <div className="border-b border-zinc-800 pb-6">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-xs md:text-sm uppercase tracking-widest text-zinc-500">
          {label}
        </h2>
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={isOpen}
          aria-controls={id}
          aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${label}`}
          className="w-8 h-8 inline-flex items-center justify-center text-zinc-500 text-lg font-mono transition-transform duration-150"
        >
          <span aria-hidden="true" style={{ transform: isOpen ? 'rotate(45deg)' : 'none' }}>+</span>
        </button>
      </div>
      {isOpen ? (
        <div id={id} className="mt-4">
          {children}
        </div>
      ) : null}
    </div>
  );
}

export function Legal() {
  const [openSections, setOpenSections] = useState({
    operating: false,
    summary: false,
    agreement: false,
    tom: false,
  });

  const toggleSection = (key) => {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const copyFullAgreement = async () => {
    await navigator.clipboard.writeText(MSA_TEXT);
  };

  const downloadAgreement = () => {
    const blob = new Blob([MSA_TEXT], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'master-services-agreement-v1.5.txt';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-zinc-950">
      <SEOHead />
      <section className="relative section-border pt-10 pb-6 md:pt-12 md:pb-8 overflow-hidden">
        <GridBg />
        <AnalyticalField variant="quiet" focalPosition="0.84,0.14" intensity={0.05} />
        <div className="container-site relative z-10 max-w-4xl">
          <div className="border-b border-zinc-800 pb-12 mb-12">
            <span className="text-xs font-mono tracking-widest uppercase text-amber-500 block mb-5">Legal</span>
            <h1 className="font-heading text-4xl md:text-5xl font-medium tracking-tight text-zinc-100 leading-tight">Legal Framework</h1>
            <p className="mt-6 text-base text-zinc-400 max-w-2xl leading-relaxed">
              Master Services Agreement (MSA) v1.5 for OnPoint.Works WFM Analytics Lab, operated by Avyria LLC.
            </p>
          </div>

          <div className="space-y-8">
            <AccordionSection
              id="operating-entity"
              label="Operating Entity"
              isOpen={openSections.operating}
              onToggle={() => toggleSection('operating')}
            >
              <p className="text-sm text-zinc-300 leading-relaxed">
                OnPoint.Works WFM Analytics Lab is operated by Avyria LLC (New Mexico, USA).
              </p>
            </AccordionSection>

            <AccordionSection
              id="legal-summary"
              label="Legal Summary"
              isOpen={openSections.summary}
              onToggle={() => toggleSection('summary')}
            >
              <ul className="space-y-3 pl-5 list-disc marker:text-zinc-500 text-sm text-zinc-300 leading-relaxed">
                {LEGAL_SUMMARY_POINTS.map((point) => (
                  <li key={point}>{point}</li>
                ))}
              </ul>
            </AccordionSection>

            <AccordionSection
              id="master-services-agreement"
              label="Master Services Agreement (v1.5)"
              isOpen={openSections.agreement}
              onToggle={() => toggleSection('agreement')}
            >
              <div className="mb-5 rounded border border-zinc-800 bg-zinc-900/60 p-4 text-xs text-zinc-400 font-mono">
                Version 1.5 • Effective Date: December 2025
              </div>
              <p className="mb-5 text-xs text-zinc-500 leading-relaxed">
                This Agreement becomes binding upon execution of a Statement of Work (SOW) or engagement of Services.
              </p>
              <div className="mb-5 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={copyFullAgreement}
                  className="inline-flex items-center rounded border border-zinc-700 bg-zinc-600 px-3 py-2 text-xs font-mono uppercase tracking-wider text-zinc-200 hover:border-zinc-600"
                >
                  Copy full agreement
                </button>
                <button
                  type="button"
                  onClick={downloadAgreement}
                  className="inline-flex items-center rounded border border-zinc-700 bg-zinc-600 px-3 py-2 text-xs font-mono uppercase tracking-wider text-zinc-200 hover:border-zinc-600"
                >
                  Download (.txt)
                </button>
              </div>
              <pre className="rounded border border-zinc-800 bg-zinc-950/70 p-5 text-[13px] leading-7 text-zinc-300 font-mono whitespace-pre-wrap break-words overflow-x-auto max-w-full">
                {MSA_TEXT}
              </pre>
            </AccordionSection>

            <AccordionSection
              id="annex-tom"
              label="Annex: Technical & Organizational Measures (TOM)"
              isOpen={openSections.tom}
              onToggle={() => toggleSection('tom')}
            >
              <pre className="rounded border border-zinc-800 bg-zinc-950/70 p-5 text-[13px] leading-7 text-zinc-300 font-mono whitespace-pre-wrap break-words overflow-x-auto max-w-full">
                {TOM_TEXT}
              </pre>
            </AccordionSection>
          </div>
        </div>
      </section>
    </div>
  );
}